// src/pages/IpBlocking.jsx
// Security → IP Blocking (3.49.0, Super Admins only; backend lib/ipGuard.js):
//   Blocked     addresses blocked automatically (failed sign-ins, too many
//               requests) or by hand (from a date to a date, or no end);
//               Unblock ends one now
//   Allowed     addresses and ranges that are never blocked (an office)
//   Failed sign-ins  the latest wrong passwords and codes, by address
//   Rules       how many failures, how long a block lasts, what counts as
//               spamming; on or off
// A blocked address can't use the portal at all (sign-in, password reset,
// every page); the fax robots (Telnyx, the email gateways) are never blocked.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import { PILL, PILL_ON, PILL_ROW } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

// Short dates (the year only when it isn't this year), kept on one line
const fmt = iso => { if (!iso) return ''; const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}), hour: 'numeric', minute: '2-digit' }); };
const KIND = { signin: 'Failed sign-ins', spam: 'Too many requests', manual: 'By hand' };
const REASON = { password: 'Password', code: 'Two-factor code', 'recovery code': 'Recovery code', 'too-many': 'Too many tries (Google)', google: 'Google', other: 'Other' };
const localInput = d => { const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return x.toISOString().slice(0, 16); };
const VIEWS = [['blocked', '⛔ Blocked'], ['allowed', '✅ Allowed'], ['failures', '🔑 Failed sign-ins'], ['rules', '⚙️ Rules']];

export default function IpBlocking() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState('blocked');
  const [busy, setBusy] = useState(false);

  const call = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}/portal/control/ip-security${path}`, {
      ...opts, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || 'Something went wrong. Please try again.');
    return j;
  }, [getToken]);
  const load = useCallback(async () => {
    try { setData(await call('')); setError(''); } catch (e) { setError(e.message); }
  }, [call]);
  useEffect(() => { load(); }, [load]);

  async function act(fn, done) {
    setBusy(true);
    try { await fn(); if (done) notify({ tone: 'success', title: done }); await load(); return true; }
    catch (e) { notify({ tone: 'error', title: "That didn't work", detail: e.message }); return false; }
    finally { setBusy(false); }
  }

  if (error && !data) return <div style={S.error}>{error}</div>;
  if (!data) return <Loader label="Loading IP blocking…" />;
  const c = data.config;
  const active = data.blocks.filter(b => b.active);
  const youAllowed = data.yourIp && data.allow.some(a => a.ip === data.yourIp);

  return (
    <div>
      <p style={S.intro}>
        A blocked address can't use the portal at all (signing in, password reset and every page), even with the right password.
        Addresses are blocked on their own after {c.failLimit} failed sign-ins in {c.failWindowMin} minutes (for {c.failBlockMin} minutes; the {ordinal(c.repeatBlocks)} block
        in {c.repeatWindowH} hours lasts {c.repeatBlockH} hours), or for spamming: over {c.spamPerMin} requests a minute (blocked {c.spamBlockH} hours; again within {c.spamRepeatDays} days,
        {' '}{c.spamRepeatBlockDays} days). The fax services (Telnyx and the email-to-fax gateways) are never blocked.
      </p>
      <div style={S.strip}>
        <span style={S.stripTitle}>IP blocking</span>
        <span>{c.on ? <strong style={{ color: 'var(--t-2e7d32)' }}>On</strong> : <strong style={{ color: 'var(--t-e65100)' }}>Off (nothing is blocked)</strong>}</span>
        <span><Emo e="⛔" /> {active.length} blocked now</span>
        <span><Emo e="✅" /> {data.allow.length} allowed</span>
        {data.yourIp && (
          <span>Your address: <code style={S.code}>{data.yourIp}</code>{' '}
            {youAllowed ? <span style={{ color: 'var(--t-2e7d32)' }}>(allowed)</span>
              : <button style={S.linkBtn} disabled={busy} onClick={() => act(() => call('/allow', { method: 'POST', body: JSON.stringify({ ip: data.yourIp, note: 'Super Admin (added from this page)' }) }), 'Your address is allowed')}>Allow it</button>}
          </span>
        )}
      </div>
      <div style={{ ...PILL_ROW, margin: '0 0 16px' }} role="tablist" aria-label="IP blocking">
        {VIEWS.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)} style={{ ...PILL, ...(view === id ? PILL_ON : {}) }}>
            <Emo e={label.split(' ')[0]} /> {label.split(' ').slice(1).join(' ')}{id === 'blocked' && active.length ? ` (${active.length})` : ''}
          </button>
        ))}
      </div>
      {view === 'blocked' && <Blocked data={data} busy={busy} act={act} call={call} confirm={confirm} />}
      {view === 'allowed' && <Allowed data={data} busy={busy} act={act} call={call} confirm={confirm} />}
      {view === 'failures' && <Failures data={data} />}
      {view === 'rules' && <Rules data={data} busy={busy} act={act} call={call} />}
    </div>
  );
}
const ordinal = n => `${n}${n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th'}`;

function Blocked({ data, busy, act, call, confirm }) {
  const [form, setForm] = useState(() => ({ ip: '', from: localInput(new Date()), until: localInput(new Date(Date.now() + 7 * 864e5)), noEnd: false, note: '' }));
  const [showEnded, setShowEnded] = useState(false);
  const rows = data.blocks.filter(b => showEnded || b.active);
  async function block(e) {
    e.preventDefault();
    const ok = await act(() => call('/blocks', { method: 'POST', body: JSON.stringify({ ip: form.ip, from: new Date(form.from).toISOString(), until: form.noEnd ? null : new Date(form.until).toISOString(), note: form.note }) }), `${form.ip.trim()} is blocked`);
    if (ok) setForm(f => ({ ...f, ip: '', note: '' }));
  }
  return (
    <div>
      <form onSubmit={block} style={S.card}>
        <div style={S.cardTitle}>Block an address</div>
        <div style={S.formRow}>
          <label style={S.field}>IP address or range<input value={form.ip} onChange={e => setForm({ ...form, ip: e.target.value })} placeholder="203.0.113.7 or 203.0.113.0/24" style={S.input} required /></label>
          <label style={S.field}>From<input type="datetime-local" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })} style={S.input} required /></label>
          <label style={S.field}>Until<input type="datetime-local" value={form.until} disabled={form.noEnd} onChange={e => setForm({ ...form, until: e.target.value })} style={{ ...S.input, opacity: form.noEnd ? 0.5 : 1 }} required={!form.noEnd} /></label>
          <label style={{ ...S.field, flexDirection: 'row', alignItems: 'center', gap: 6, paddingBottom: 9 }}><input type="checkbox" checked={form.noEnd} onChange={e => setForm({ ...form, noEnd: e.target.checked })} /> No end</label>
          <label style={{ ...S.field, flex: '1 1 200px' }}>Note (optional)<input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Why it's blocked" style={S.input} maxLength={300} /></label>
          <button type="submit" disabled={busy || !form.ip.trim()} style={{ ...S.primary, opacity: busy || !form.ip.trim() ? 0.6 : 1 }}><Emo e="⛔" /> Block</button>
        </div>
      </form>
      <label style={S.toggle}><input type="checkbox" checked={showEnded} onChange={e => setShowEnded(e.target.checked)} /> Show blocks that have ended too</label>
      <Table head={['Address', 'Why', 'From', 'Until', 'Status', 'Note', '']} empty={showEnded ? 'No address has been blocked yet.' : 'No address is blocked right now.'}
        rows={rows.map(b => [
          <code style={S.code}>{b.ip}</code>,
          <span>{KIND[b.kind] || b.kind}<div style={S.sub}>{b.kind === 'manual' ? `by ${b.by}` : b.reason}</div>{(b.history || []).length > 1 && <div style={S.sub}>{b.history.length} blocks so far</div>}</span>,
          fmt(b.from), b.until ? fmt(b.until) : <strong>No end</strong>,
          b.active ? <span style={{ ...S.badge, ...S.badgeRed }}>Blocked</span> : <span style={{ ...S.badge, ...S.badgeGrey }}>{b.unblockedAt ? `Unblocked by ${b.unblockedBy}` : 'Ended'}</span>,
          b.note || '',
          b.active ? <button style={S.rowBtn} disabled={busy} onClick={async () => {
            if (await confirm({ title: `Unblock ${b.ip}?`, message: 'It can use the portal again straight away.', confirmLabel: 'Unblock' })) act(() => call(`/blocks/${encodeURIComponent(b.id)}`, { method: 'DELETE' }), `${b.ip} is unblocked`);
          }}>Unblock</button> : '',
        ])} />
    </div>
  );
}

function Allowed({ data, busy, act, call, confirm }) {
  const [form, setForm] = useState({ ip: '', note: '' });
  async function add(e) {
    e.preventDefault();
    const ok = await act(() => call('/allow', { method: 'POST', body: JSON.stringify(form) }), `${form.ip.trim()} is allowed`);
    if (ok) setForm({ ip: '', note: '' });
  }
  return (
    <div>
      <form onSubmit={add} style={S.card}>
        <div style={S.cardTitle}>Allow an address <span style={S.sub}>Never blocked automatically, however many failed sign-ins. Use it for an office or your own network.</span></div>
        <div style={S.formRow}>
          <label style={S.field}>IP address or range<input value={form.ip} onChange={e => setForm({ ...form, ip: e.target.value })} placeholder="203.0.113.7 or 203.0.113.0/24" style={S.input} required /></label>
          <label style={{ ...S.field, flex: '1 1 240px' }}>Note (optional)<input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="e.g. Clinicmind office" style={S.input} maxLength={300} /></label>
          <button type="submit" disabled={busy || !form.ip.trim()} style={{ ...S.primary, opacity: busy || !form.ip.trim() ? 0.6 : 1 }}><Emo e="✅" /> Allow</button>
        </div>
      </form>
      <Table head={['Address', 'Note', 'Added', '']} empty="No address is on the allow list."
        rows={data.allow.map(a => [
          <code style={S.code}>{a.ip}</code>, a.note || '', <span>{fmt(a.at)}<div style={S.sub}>by {a.by}</div></span>,
          <button style={S.rowBtn} disabled={busy} onClick={async () => {
            if (await confirm({ title: `Take ${a.ip} off the allow list?`, message: 'It can then be blocked like any other address.', confirmLabel: 'Take it off', danger: true })) act(() => call(`/allow/${encodeURIComponent(a.id)}`, { method: 'DELETE' }), `${a.ip} is off the allow list`);
          }}>Remove</button>,
        ])} />
    </div>
  );
}

function Failures({ data }) {
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.failures.filter(f => !t || `${f.ip} ${f.email}`.toLowerCase().includes(t));
  }, [data.failures, q]);
  const byIp = useMemo(() => Object.entries(rows.reduce((m, f) => { m[f.ip] = (m[f.ip] || 0) + 1; return m; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5), [rows]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find an address or email" style={{ ...S.input, width: 260 }} aria-label="Find an address or email" />
        <span style={S.sub}>The latest {data.failures.length} failed sign-ins (kept 90 days).{byIp.length ? ' Most from: ' : ''}{byIp.map(([ip, n]) => `${ip} (${n})`).join(', ')}</span>
      </div>
      <Table head={['When', 'Address', 'Email typed', 'What failed', 'Where']} empty="No failed sign-ins."
        rows={rows.map(f => [fmt(f.at), <span><code style={S.code}>{f.ip}</code>{f.allowed && <div style={S.sub}>allowed address</div>}</span>, f.email || '—', REASON[f.reason] || f.reason, f.source])} />
    </div>
  );
}

const RULES = [
  ['Failed sign-ins', [['failLimit', 'Failed sign-ins before a block'], ['failWindowMin', '… within (minutes)'], ['failBlockMin', 'Block for (minutes)']]],
  ['Repeated blocks', [['repeatBlocks', 'This many blocks…'], ['repeatWindowH', '… within (hours)'], ['repeatBlockH', '… then block for (hours)']]],
  ['Spamming', [['spamPerMin', 'Requests a minute that count as spamming'], ['spamBlockH', 'Block for (hours)'], ['spamRepeatDays', 'Again within (days)…'], ['spamRepeatBlockDays', '… then block for (days)']]],
];
function Rules({ data, busy, act, call }) {
  const [form, setForm] = useState(data.config);
  const changed = JSON.stringify(form) !== JSON.stringify(data.config);
  const save = () => act(() => call('/rules', { method: 'PUT', body: JSON.stringify(form) }), 'Rules saved');
  return (
    <div style={S.card}>
      <label style={{ ...S.toggle, marginBottom: 14 }}>
        <input type="checkbox" checked={form.on !== false} onChange={e => setForm({ ...form, on: e.target.checked })} />
        <strong>IP blocking is on</strong> <span style={S.sub}>Off: nothing is blocked automatically or by hand (the lists are kept).</span>
      </label>
      {RULES.map(([title, fields]) => (
        <div key={title} style={{ marginBottom: 14 }}>
          <div style={S.cardTitle}>{title}</div>
          <div style={S.formRow}>
            {fields.map(([k, label]) => (
              <label key={k} style={S.field}>{label}
                <input type="number" value={form[k]} min={data.limits[k][0]} max={data.limits[k][1]} onChange={e => setForm({ ...form, [k]: e.target.value === '' ? '' : Number(e.target.value) })} style={{ ...S.input, width: 140 }} />
                {form[k] !== data.defaults[k] && <span style={S.sub}>Usual: {data.defaults[k]}</span>}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={save} disabled={busy || !changed} style={{ ...S.primary, opacity: busy || !changed ? 0.6 : 1 }}>Save rules</button>
        <button onClick={() => setForm({ ...data.defaults })} disabled={busy} style={S.rowBtn}>Back to the usual rules</button>
      </div>
    </div>
  );
}

function Table({ head, rows, empty }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead><tr style={S.thead}>{head.map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={head.length} style={S.empty}>{empty}</td></tr>
            : rows.map((r, i) => (
              <tr key={i} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                {r.map((cell, j) => <td key={j} style={typeof cell === 'string' && /^[A-Z][a-z]{2} \d/.test(cell) ? { ...S.td, whiteSpace: 'nowrap' } : S.td}>{cell}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

const S = {
  intro: { color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px', maxWidth: 980 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, fontSize: 14 },
  strip: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, fontSize: 13, color: 'var(--t-37474f)' },
  stripTitle: { fontWeight: 700, color: 'var(--t-00695c)', fontSize: 14 },
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--b-e8f5e9)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)' },
  cardTitle: { fontWeight: 700, color: 'var(--t-00695c)', fontSize: 14, marginBottom: 10 },
  formRow: { display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)' },
  input: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  primary: { padding: '9px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  rowBtn: { padding: '6px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  linkBtn: { border: 'none', background: 'none', color: 'var(--t-00796b)', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, padding: 0 },
  toggle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '0 0 10px', flexWrap: 'wrap' },
  sub: { fontSize: 11.5, color: 'var(--t-78909c)', fontWeight: 400 },
  code: { fontSize: 12, color: 'var(--t-37474f)', background: 'var(--c-eceff1)', padding: '2px 6px', borderRadius: 4 },
  badge: { padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  badgeRed: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)' },
  badgeGrey: { background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  empty: { textAlign: 'center', padding: '36px 0', color: 'var(--t-78909c)', fontSize: 13 },
};
